// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal } from "react-bootstrap";

import { ModalPanel } from "states/guistates";
import { ActionType, IAction, IActionChangeTrend, raiseAction } from "actions";
import { IState } from "states/states";
import { DataState } from "types";
import { isSet } from "utils";
import { ITrendDataRow } from "states/trendingstates";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp//FormattedTime";
import SaveFeedback from "view/comp/SaveFeedback";

type IModalTrendItemProps = {
  timestamp: number | undefined;
  tlIndex: number | undefined;
  tlName: string;
  isRegulation: boolean;
  isOpen: boolean;
  dataState: DataState;
  curData: ITrendDataRow | undefined;
};

const mapStateToProps = (state: IState) => {
  const timestamp = state.gui.modalTrendData?.timestamp;
  const tlIndex = state.gui.modalTrendData?.tlIndex;
  const tariffCircuit = isSet(tlIndex) ? state.parameter?.tariffline[tlIndex] : undefined;
  const curData =
    timestamp && isSet(tlIndex) && state.trending.trendData[timestamp]
      ? state.trending.trendData[timestamp][tlIndex]
      : undefined;

  const props: IModalTrendItemProps = {
    timestamp,
    tlIndex,
    isOpen: state.gui.modal.panel === ModalPanel.TREND && isSet(timestamp) && isSet(tariffCircuit),
    dataState: state.gui.modal.dataState,
    tlName: tariffCircuit?.name ?? "",
    isRegulation: isSet(curData && curData.targetpower),
    curData,
  };
  return props;
};

interface IModalTrendState {
  wasOpen: boolean;
  regPeriod?: number;
  energy?: number;
  costs?: number;
  targetValue?: number;
}

interface IModalTrendItemDispatch {
  onOkClick: (
    timestamp: number,
    tlId: number,
    curData: ITrendDataRow,
    state: IModalTrendState,
  ) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalTrendItemDispatch = {
    onOkClick: (
      timestamp: number,
      tlIndex: number,
      curData: ITrendDataRow,
      state: IModalTrendState,
    ) => {
      const payload: IActionChangeTrend = {
        timestamp,
        tlIndex,
        regperiod: state.regPeriod ?? curData.regperiod!,
        energy: state.energy ?? curData.energy!,
        costs: state.costs ?? curData.costs,
        tariff: curData.tariff, // not changeable by user yet
        targetpower: state.targetValue ?? curData.targetpower,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_TREND_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class ModalTrendItemComponent extends React.Component<
  IModalTrendItemProps & IModalTrendItemDispatch & WrappedComponentProps,
  IModalTrendState
> {
  state: IModalTrendState = {
    wasOpen: false,
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
        regPeriod: undefined,
        energy: undefined,
        costs: undefined,
        targetValue: undefined,
      });
    }
  }

  public render() {
    if (!this.props.isOpen) {
      return <></>; // avoid rendering of inexisting data
    }

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header>
          <FormattedMessage id="Trend Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelTrend"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="saveTrend"
            variant="primary"
            onClick={this.props.onOkClick.bind(
              this,
              this.props.timestamp!,
              this.props.tlIndex!,
              this.props.curData!,
              this.state,
            )}
            disabled={
              this.props.dataState === DataState.SAVE_REQ ||
              (!isSet(this.state.regPeriod) &&
                !isSet(this.state.energy) &&
                !isSet(this.state.costs) &&
                !isSet(this.state.targetValue))
            }
          >
            <FormattedMessage id="Save" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    const inputStyle: React.CSSProperties = { minWidth: 120 };

    return (
      <>
        <p>
          <FormattedMessage id="Changing the values of this trend item:" />
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                <FormattedMessage id="Timestamp" />
              </td>
              <td>
                <FormattedDate valueMs={this.props.timestamp! * 1000} />{" "}
                <FormattedTime valueMs={this.props.timestamp! * 1000} />
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Tariffcircuit" />
              </td>
              <td>{this.props.tlName}</td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Regulation Period" />
              </td>
              <td>
                <input
                  type="number"
                  value={this.state.regPeriod ?? this.props.curData?.regperiod}
                  onChange={(event) => {
                    this.setState({ regPeriod: event?.currentTarget.valueAsNumber });
                  }}
                  style={inputStyle}
                />{" "}
                s
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Energy" />
              </td>
              <td>
                <input
                  type="number"
                  value={this.state.energy ?? this.props.curData?.energy}
                  onChange={(event) => {
                    this.setState({ energy: event?.currentTarget.valueAsNumber });
                  }}
                  style={inputStyle}
                />{" "}
                kWh
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Costs Energy" />
              </td>
              <td>
                <input
                  type="number"
                  value={this.state.costs ?? this.props.curData?.costs ?? undefined}
                  onChange={(event) => {
                    this.setState({ costs: event?.currentTarget.valueAsNumber });
                  }}
                  style={inputStyle}
                />{" "}
                EUR
              </td>
            </tr>
            {this.props.isRegulation && (
              <tr>
                <td>
                  <FormattedMessage id="Target Value" />
                </td>
                <td>
                  <input
                    type="number"
                    value={this.state.targetValue ?? this.props.curData?.targetpower}
                    onChange={(event) => {
                      this.setState({ targetValue: event?.currentTarget.valueAsNumber });
                    }}
                    style={inputStyle}
                  />{" "}
                  kW
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    );
  }
}

const ModalTrendItem = connect(mapStateToProps, mapDispatchToProps)(ModalTrendItemComponent);

export default injectIntl(ModalTrendItem);
